import React from "react";
import { Link } from "react-router-dom";
import CategoryComparisonTable, { useSubcategoryItems } from "../../components/CategoryComparisonTable";
import UART_COMPARE_ITEMS from "./uartCompareData";
import "./LandingDisplay.css";
import { heroSrcSet } from "../../utils/heroSrcSet";

/**
 * /uart-display -- landing page for the Smart UART (HMI) display line.
 *
 * Static, prerendered marketing page (see prerender.js STATIC_ROUTES and
 * src/seo/pageMeta.js for the <head> tags and Product/FAQ JSON-LD). The
 * Firestore-driven grid at /category/special-displays/smart-uart-display
 * stays as the full variant list; this page targets the searches people
 * actually type -- "hmi display", "nextion alternative", "dwin display",
 * "esp32 display", "arduino tft display" -- which the word UART never does.
 *
 * Prices and part numbers mirror the live Tixaru catalog as of Oct 2026.
 */

export const UART_HERO_PRODUCT = {
  pn: "B-1102-VUI-0700-BX",
  name: "7 inch Smart UART display with capacitive touch",
  image: "/landing/uart-display-7-inch-b-1102.jpg",
  route: "/tft/special-displays/smart-uart-display/b-1102-vui-0700-bx",
  datasheet:
    "https://tixaru.in/api/v1/public/catalog/attachments/2b905c4d-424b-4b6d-9c87-ecb6a778802a/file",
  priceSingle: 5000,
  priceFrom: 4500,
  slabs: [
    ["1 - 10", 5000],
    ["11 - 50", 4800],
    ["51 - 100", 4700],
    ["101 - 250", 4500],
  ],
};

const VARIANTS = [
  { pn: "B-1102-VUI-0700-BX", size: "7 inch", touch: "Capacitive", nits: "400", note: "IPS, stocked, Dsync Studio ready", route: UART_HERO_PRODUCT.route },
  { pn: "B-1103-VUI-0700-BX", size: "7 inch", touch: "Capacitive", nits: "1000", note: "IPS, sunlight readable" },
  { pn: "B-1100-VUN-0700-BX", size: "7 inch", touch: "Optional", nits: "1000", note: "IPS, no touch or add CTP" },
  { pn: "B-1101-QUI-0500-BX", size: "5 inch", touch: "Capacitive", nits: "400", note: "Full viewing angle" },
  { pn: "B-1126-VUI-0500-BX", size: "5 inch", touch: "Capacitive", nits: "1000", note: "Sunlight readable" },
  { pn: "B-2289-PUN-0500-BX", size: "5 inch", touch: "Resistive", nits: "400", note: "Glove and stylus friendly" },
  { pn: "B-2290-PUN-0500-BX", size: "5 inch", touch: "Resistive", nits: "1000", note: "Outdoor, resistive" },
  { pn: "B-1098-VUI-0500-BX", size: "5 inch", touch: "None", nits: "400", note: "Display only" },
  { pn: "B-2292-TUN-0500-BX", size: "5 inch", touch: "None", nits: "1000", note: "Outdoor, display only" },
];

const CATEGORY_ROUTE = "/category/special-displays/smart-uart-display";

const inr = (n) => "₹" + n.toLocaleString("en-IN");

const ICONS = {
  chip: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="4" width="16" height="16" rx="2" /><rect x="9" y="9" width="6" height="6" />
      <path d="M9 1v3M15 1v3M9 20v3M15 20v3M20 9h3M20 14h3M1 9h3M1 14h3" />
    </svg>
  ),
  wire: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 12h4l3-9 4 18 3-9h2" />
    </svg>
  ),
  design: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 21V9" />
    </svg>
  ),
  wifi: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12.55a11 11 0 0 1 14.08 0" /><path d="M1.42 9a16 16 0 0 1 21.16 0" />
      <path d="M8.53 16.11a6 6 0 0 1 6.95 0" /><circle cx="12" cy="20" r="1" />
    </svg>
  ),
  sun: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="12" r="4" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M4.9 19.1 7 17M17 7l2.1-2.1" />
    </svg>
  ),
  factory: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 20a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8l-7 5V8l-7 5V4a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2z" />
    </svg>
  ),
};

const FEATURES = [
  {
    icon: ICONS.chip,
    title: "Renders its own UI on an ESP32-S3",
    desc: "The display carries an ESP32-S3 with PSRAM that draws every screen, handles touch and runs animations. Your controller never pushes pixels.",
  },
  {
    icon: ICONS.wire,
    title: "Two wires to any microcontroller",
    desc: "TX, RX and power. Send a value over UART at 115200 baud and the gauge, text or progress bar updates. Touch events come back the same way.",
  },
  {
    icon: ICONS.design,
    title: "No-code UI in Dsync Studio",
    desc: "Drag buttons, sliders, curves and images onto a canvas, bind them to variables, flash over USB. No LVGL, no font tables, no graphics driver to write.",
  },
  {
    icon: ICONS.wifi,
    title: "Wi-Fi and Bluetooth LE on board",
    desc: "Because the brain is an ESP32-S3 you get wireless connectivity, an SD card slot and a speaker output on the same module.",
  },
  {
    icon: ICONS.sun,
    title: "400 or 1000 nit IPS, 5 or 7 inch",
    desc: "Capacitive, resistive or no touch. 1000 nit variants read in direct sunlight for agricultural, solar and outdoor equipment.",
  },
  {
    icon: ICONS.factory,
    title: "Designed and built in India",
    desc: "A DWIN or Nextion alternative with local stock, INR pricing, GST invoices and an engineering team in Gurugram that answers the phone.",
  },
];

const COMPAT = [
  "Arduino Uno / Mega / Nano",
  "ESP32 and ESP8266",
  "STM32",
  "Raspberry Pi Pico",
  "PIC and AVR",
  "Renesas and NXP MCUs",
  "Raspberry Pi (via UART)",
  "Any 3.3 V or 5 V UART",
];

const FAQ = [
  {
    q: "What is a Smart UART display?",
    a: "A TFT touch display with its own processor and firmware. You design the screens on a PC, load them onto the display, and your microcontroller only sends short serial commands such as a temperature value or a page change. The display draws everything itself. The category is also called a serial HMI display or an intelligent display module.",
  },
  {
    q: "Is this a replacement for Nextion or DWIN displays?",
    a: "Yes, it serves the same purpose. The difference is that the BIBA Smart UART display is designed and made in India, stocked in Gurugram, priced in INR with GST invoices, and the design tool Dsync Studio is free. Projects written for other serial displays are ported by rebuilding the screens in Dsync Studio and mapping the same variables.",
  },
  {
    q: "Which microcontrollers can drive it?",
    a: "Anything with a UART: Arduino, ESP32, STM32, Raspberry Pi Pico, PIC, AVR and Linux boards such as Raspberry Pi. Logic levels of 3.3 V and 5 V are both supported.",
  },
  {
    q: "How do I design the user interface?",
    a: "With Dsync Studio, BIBA's free Windows application. Place widgets on a drag-and-drop canvas, bind them to named variables, preview with the built-in simulator, then flash the project to the display over USB. See the Dsync Studio feature page for screenshots.",
  },
  {
    q: "What does the 7 inch model cost?",
    a: "Part B-1102-VUI-0700-BX is " + inr(5000) + " for 1 to 10 pieces and " + inr(4500) + " at 101 pieces and above, excluding GST. It is stocked for online ordering.",
  },
  {
    q: "What is the difference between the 5 inch and 7 inch Smart UART display?",
    a: "Both use an 800 x 480 IPS panel, the same ESP32-S3 controller and the same Dsync Studio toolchain, so a design moves between them with a layout change. The 5 inch has an active area of 108.0 x 64.8 mm and the 7 inch has 153.84 x 85.63 mm. Each size is available in 400 nit and 1000 nit versions.",
  },
  {
    q: "Which touch and brightness options are available?",
    a: "Capacitive touch (CTP), resistive touch (RTP) or no touch, in 400 nit and 1000 nit brightness. Choose 1000 nit for direct sunlight and outdoor equipment, and resistive touch when operators wear gloves or use a stylus. The 7 inch B-1100-VUN-0700-BX has optional touch.",
  },
  {
    q: "Is Dsync Studio free, and which computers does it run on?",
    a: "Yes. Dsync Studio is free to download from the Microsoft Store and runs on Windows 10 and Windows 11. It is a no-code tool, so you place widgets on a canvas instead of writing display firmware.",
  },
  {
    q: "What supply voltage and logic levels does the display use?",
    a: "The UART accepts 3.3 V and 5 V logic, so it connects directly to Arduino, ESP32, STM32, Raspberry Pi Pico and similar boards. The display itself runs from a 5 V or 12 V supply.",
  },
  {
    q: "Can I use a Smart UART display without writing any graphics code?",
    a: "Yes. The screens are designed and stored on the display. Your firmware only sends short serial commands, for example a value for a gauge or a page number, and receives touch events back. There is no graphics library, font handling or touch calibration code in your microcontroller project.",
  },
  {
    q: "Can I buy a single unit, and does BIBA ship across India?",
    a: "Yes. Single units can be ordered online for evaluation with INR pricing and GST invoices, and volume pricing is published for the 7 inch model. Orders ship from BIBA's facility in Gurugram, Haryana. For OEM volumes and custom versions, request a quote.",
  },
  {
    q: "Can BIBA customise the display for my product?",
    a: "Yes. We supply custom brightness, cover glass, cable lengths and mounting for OEM volumes, and because BIBA is also an EMS company we can build the complete control board around the display in our Gurugram facility.",
  },
];

export { FAQ as UART_FAQ, VARIANTS as UART_VARIANTS };

export default function UartDisplayPage() {
  const hero = UART_HERO_PRODUCT;
  // Live specs and prices for every Smart UART model; until they load (and in
  // the prerendered HTML) the static variant table below stands in.
  const liveItems = useSubcategoryItems("special-displays", "smart-uart-display");
  const compareItems = liveItems.length > 1 ? liveItems : UART_COMPARE_ITEMS;
  return (
    <div className="ld-root">
      {/* Hero */}
      <section className="ld-hero">
        <div className="ld-wrap ld-hero-grid">
          <div>
            <span className="ld-eyebrow">Smart UART displays</span>
            <h1 className="ld-h1">
              <em>HMI touch display</em> for Arduino, ESP32 and STM32, driven over UART
            </h1>
            <p className="ld-lead">
              5 inch and 7 inch 800 x 480 IPS displays with an ESP32-S3 on board. Design the screens in free
              Dsync Studio, then update them from your microcontroller with two wires. An Indian-made
              alternative to Nextion and DWIN, stocked in Gurugram.
            </p>
            <div className="ld-cta-row">
              <Link to={hero.route} className="ld-btn ld-btn-primary">
                Buy the 7 inch from {inr(hero.priceFrom)}
              </Link>
              <Link to="/dsync-studio-features" className="ld-btn ld-btn-secondary">
                See Dsync Studio
              </Link>
              <a href="#compare" className="ld-btn ld-btn-secondary">
                Compare all models
              </a>
            </div>
            <div className="ld-stats">
              <div className="ld-stat"><span className="ld-stat-val">800 x 480</span><span className="ld-stat-lbl">Resolution, IPS</span></div>
              <div className="ld-stat"><span className="ld-stat-val">ESP32-S3</span><span className="ld-stat-lbl">On-board processor</span></div>
              <div className="ld-stat"><span className="ld-stat-val">UART</span><span className="ld-stat-lbl">2-wire control</span></div>
              <div className="ld-stat"><span className="ld-stat-val">-20 to 70 C</span><span className="ld-stat-lbl">Operating range</span></div>
            </div>
          </div>
          <div className="ld-hero-media">
            <img
              src={hero.image}
              srcSet={heroSrcSet(hero.image)}
              sizes="(max-width: 900px) 92vw, 600px"
              fetchPriority="high"
              alt="7 inch Smart UART HMI display showing Dsync Studio widgets: image, button, animation, slider, switch, text, progress bar and curve"
              width="1400"
              height="933"
            />
            <p className="ld-hero-caption">7 inch Smart UART display, part number {hero.pn}, running a Dsync Studio widget demo</p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="ld-section ld-section-alt">
        <div className="ld-wrap">
          <div className="ld-section-head">
            <h2 className="ld-h2">From blank screen to working HMI in an afternoon</h2>
            <p className="ld-sub">No graphics library on your microcontroller, no font rendering, no touch calibration code.</p>
          </div>
          <div className="ld-steps">
            <div className="ld-step">
              <span className="ld-step-num">1</span>
              <h3>Design in Dsync Studio</h3>
              <p>Drop gauges, buttons, sliders, text and images onto screens. Name the variables each widget listens to. Test with the built-in simulator.</p>
            </div>
            <div className="ld-step">
              <span className="ld-step-num">2</span>
              <h3>Flash over USB</h3>
              <p>One click sends the project to the ESP32-S3 on the display. Fonts, images and animations live on the display, not on your controller.</p>
            </div>
            <div className="ld-step">
              <span className="ld-step-num">3</span>
              <h3>Wire TX, RX and power</h3>
              <p>Connect the display UART to any microcontroller UART. Send a value, the widget updates. Touch a button, your controller receives the event.</p>
            </div>
            <div className="ld-step">
              <span className="ld-step-num">4</span>
              <h3>Ship it</h3>
              <p>The same part number is stocked for prototypes and supplied in volume, with 400 or 1000 nit and touch options on the same footprint.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison of every model */}
      <section className="ld-section" id="models">
        <div className="ld-wrap">
          <div className="ld-compare-live" id="compare">
            <CategoryComparisonTable
              heading="Compare all Smart UART displays"
              items={compareItems}
              hideLabels={["TOP POLARIZER TYPE", "TFT PIN COUNT"]}
            />
          </div>
          <div className="ld-crosslink">
            All variants share the 800 x 480 panel, the ESP32-S3 controller and the Dsync Studio toolchain, so a
            project built on the 7 inch runs on the 5 inch with a layout change.{" "}
            <Link to={CATEGORY_ROUTE}>Switch to the product grid</Link> or{" "}
            <Link to="/contact">ask us which one fits</Link>.
          </div>
        </div>
      </section>

      {/* Dsync Studio */}
      <section className="ld-section ld-section-alt" id="dsync-studio">
        <div className="ld-wrap ld-two-col">
          <div className="ld-prose">
            <span className="ld-eyebrow">Free HMI design software</span>
            <h2 className="ld-h2">Design the screens in Dsync Studio</h2>
            <p>
              Dsync Studio is BIBA's no-code HMI design software for Smart UART displays. It runs on Windows 10 and
              Windows 11 and is free from the Microsoft Store. Build a touchscreen interface on a drag-and-drop canvas,
              connect each widget to a named variable, test it on your PC, and flash it to the display over USB.
            </p>
            <ul className="ld-bullets">
              <li><strong>Drag-and-drop canvas:</strong> buttons, sliders, gauges, curves, charts, keypads, text and images.</li>
              <li><strong>Live UART data binding:</strong> a value sent over serial updates the widget, a touch sends an event back.</li>
              <li><strong>Built-in data simulator:</strong> preview the behaviour before any hardware is connected.</li>
              <li><strong>Board catalog:</strong> pick your display and the resolution, touch and flash package are configured for you.</li>
              <li><strong>One-click ESP32 flashing:</strong> send the finished project to the display over USB.</li>
              <li><strong>Templates, PSD import and multi-screen projects</strong> to get from a mock-up to a working interface quickly.</li>
            </ul>
            <div className="ld-cta-row">
              <Link to="/dsync-studio-features" className="ld-btn ld-btn-primary">Explore Dsync Studio features</Link>
              <a
                href="https://apps.microsoft.com/detail/9MZ09MB4D61Z?hl=en-us&gl=IN&ocid=pdpshare"
                className="ld-btn ld-btn-secondary"
                target="_blank"
                rel="noopener noreferrer"
              >
                Download free from Microsoft Store
              </a>
            </div>
          </div>
          <div className="ld-dsync-media">
            <img
              src="/logo/dsync-studio.png"
              alt="Dsync Studio HMI design software with the widget canvas, property panel and live preview for a Smart UART display"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Which model */}
      <section className="ld-section">
        <div className="ld-wrap">
          <div className="ld-section-head">
            <h2 className="ld-h2">Which Smart UART display should you choose?</h2>
            <p className="ld-sub">Pick by how the display is used. Every model has the same 800 x 480 IPS panel and ESP32-S3 controller.</p>
          </div>
          <div className="ld-table-wrap">
            <table className="ld-table">
              <thead>
                <tr><th>If you need</th><th>Choose</th><th>Why</th></tr>
              </thead>
              <tbody>
                <tr>
                  <td>To evaluate and prototype</td>
                  <td><Link to={UART_HERO_PRODUCT.route}>B-1102-VUI-0700-BX</Link> (7 inch)</td>
                  <td>Stocked for online ordering, capacitive touch, 400 nit, published volume pricing.</td>
                </tr>
                <tr>
                  <td>Readable in direct sunlight</td>
                  <td>B-1103-VUI-0700-BX (7 inch), B-1126-VUI-0500-BX (5 inch)</td>
                  <td>1000 nit IPS with capacitive touch for outdoor, solar and agricultural equipment.</td>
                </tr>
                <tr>
                  <td>Gloves, stylus or wet hands</td>
                  <td>B-2289-PUN-0500-BX (400 nit), B-2290-PUN-0500-BX (1000 nit)</td>
                  <td>5 inch resistive touch that works with gloves and a stylus.</td>
                </tr>
                <tr>
                  <td>A compact panel</td>
                  <td>B-1101-QUI-0500-BX (5 inch)</td>
                  <td>108.0 x 64.8 mm active area with capacitive touch and a full viewing angle.</td>
                </tr>
                <tr>
                  <td>Display only, no touch</td>
                  <td>B-1098-VUI-0500-BX, B-2292-TUN-0500-BX (5 inch)</td>
                  <td>For read-out panels where a separate keypad or encoder does the input.</td>
                </tr>
                <tr>
                  <td>Touch as an option</td>
                  <td>B-1100-VUN-0700-BX (7 inch)</td>
                  <td>1000 nit 7 inch with optional touch.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Smart UART vs alternatives */}
      <section className="ld-section ld-section-alt">
        <div className="ld-wrap">
          <div className="ld-section-head">
            <h2 className="ld-h2">Smart UART display vs bare TFT vs HDMI display</h2>
            <p className="ld-sub">The three common ways to put a touchscreen on a product, and when each one fits.</p>
          </div>
          <div className="ld-table-wrap">
            <table className="ld-table">
              <thead>
                <tr><th></th><th>Smart UART display</th><th>Bare RGB / SPI TFT</th><th>HDMI display</th></tr>
              </thead>
              <tbody>
                <tr><td>Who draws the screen</td><td>The display (ESP32-S3)</td><td>Your microcontroller</td><td>A Linux or Windows computer</td></tr>
                <tr><td>Connection to your controller</td><td>2-wire UART plus power</td><td>Many parallel or SPI signals</td><td>HDMI plus USB touch</td></tr>
                <tr><td>Graphics work in your firmware</td><td>None, only serial commands</td><td>Driver, frame buffer, GUI library</td><td>A full GUI application</td></tr>
                <tr><td>Design tool</td><td>Dsync Studio, no code</td><td>LVGL or custom code</td><td>Any desktop UI framework</td></tr>
                <tr><td>Best with</td><td>Arduino, ESP32, STM32, PIC, PLCs</td><td>Fast MCUs with spare RAM</td><td>Raspberry Pi, Jetson, industrial PCs</td></tr>
              </tbody>
            </table>
          </div>
          <div className="ld-crosslink">
            Looking for a display for a Raspberry Pi or PC? See the <Link to="/hdmi-display">HDMI touch displays</Link>.
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="ld-section ld-section-alt">
        <div className="ld-wrap">
          <div className="ld-section-head">
            <h2 className="ld-h2">Why engineers pick a Smart UART display over a bare TFT</h2>
            <p className="ld-sub">
              A bare RGB or SPI TFT needs a capable MCU, a frame buffer and weeks of GUI work. A Smart UART display
              moves all of that onto the display and leaves your firmware with a handful of serial writes.
            </p>
          </div>
          <div className="ld-features">
            {FEATURES.map((f) => (
              <div className="ld-feature" key={f.title}>
                <div className="ld-feature-icon">{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compatibility + comparison */}
      <section className="ld-section">
        <div className="ld-wrap ld-two-col">
          <div className="ld-prose">
            <h2 className="ld-h2">Works with</h2>
            <p>
              Any controller with a serial port. Typical applications are water purifier and air purifier panels,
              battery and solar monitors, EV chargers, lab instruments, agricultural controllers, commercial kitchen
              equipment and industrial machine HMIs where a PLC or a small MCU already runs the process.
            </p>
            <div className="ld-chips">
              {COMPAT.map((c) => <span className="ld-chip" key={c}>{c}</span>)}
            </div>
          </div>
          <aside className="ld-aside">
            <h3>Smart UART or HDMI?</h3>
            <ul>
              <li><strong>Smart UART display:</strong> the display renders its own UI; you send values over serial. Best with Arduino, ESP32, STM32, PIC and PLC-class controllers.</li>
              <li><strong>HDMI display:</strong> a Linux or Windows board renders the UI. Best with Raspberry Pi, Jetson and industrial PCs.</li>
            </ul>
            <p style={{ marginTop: 12, marginBottom: 0 }}>
              <Link to="/hdmi-display" className="ld-btn ld-btn-secondary">See HDMI displays</Link>
            </p>
          </aside>
        </div>
      </section>

      {/* FAQ */}
      <section className="ld-section ld-section-alt">
        <div className="ld-wrap">
          <div className="ld-section-head">
            <h2 className="ld-h2">Frequently asked questions</h2>
          </div>
          <div className="ld-faq">
            {FAQ.map((f) => (
              <details key={f.q}>
                <summary>{f.q}</summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="ld-section">
        <div className="ld-wrap">
          <div className="ld-cta">
            <div>
              <h2>Evaluate one, then scale to your volume</h2>
              <p>
                Order the 7 inch online today, download Dsync Studio free, and talk to our engineers when you are
                ready for a custom brightness, cover glass or a complete HMI board built around the display.
              </p>
            </div>
            <div className="ld-cta-actions">
              <Link to={hero.route} className="ld-btn ld-btn-primary">Buy 7 inch</Link>
              <Link to="/dsync-studio-features" className="ld-btn ld-btn-secondary">Dsync Studio</Link>
              <Link to="/contact" className="ld-btn ld-btn-secondary">Request a quote</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
